import React, { useEffect, useRef, useState } from "react"
import '../styles/AdminTopbar.css'

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)
const BellIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
)
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="14" height="14" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="17" y2="10"/>
  </svg>
)
const ChevronDown = () => (
  <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor">
    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
  </svg>
)

const StatusBadge = ({ status }) => {
  const color = status === 'in_progress' ? '#F59E0B'
    : status === 'resolved' ? '#10B981'
    : status === 'closed' ? '#94A3B8'
    : '#3B82F6'
  const label = status === 'in_progress' ? 'In Progress'
    : status === 'resolved' ? 'Resolved'
    : status === 'closed' ? 'Closed'
    : status || '—'
  return (
    <span style={{ fontSize: '10px', fontWeight: 600, color, background: `${color}18`, borderRadius: '4px', padding: '2px 6px' }}>
      {label}
    </span>
  )
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const DATE_OPTIONS = [
  { period: 'today',    label: 'Today' },
  { period: '7days',   label: 'Last 7 Days' },
  { period: '30days',  label: 'Last 30 Days' },
  { period: '6months', label: 'Last 6 Months' },
]

const pageTitle = {
  agent: 'Agent Workspace',
  tickets: 'Ticket 360',
  'ticket-detail': 'Ticket Detail',
  create: 'Create Ticket',
  'vehicle-360': 'Vehicle 360',
}

const AdminTopbar = ({
  currentPage,
  setCurrentPage,
  notifications,
  setNotifications,
  fetchJson,
  dateFilter,
  setDateFilter,
  setSelectedTicket,
  setSelectedVehicle,
  dialerOpen,
  setDialerOpen,
  sidebarOpen,
  setSidebarOpen,
  clearCallState,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [prevTicketModal, setPrevTicketModal] = useState(null)
  const searchRef = useRef(null)
  const dateRef = useRef(null)

  function handleCreateTicketClick() {
    try {
      const stored = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
      if (stored?.ticket_id || stored?.id) {
        setPrevTicketModal(stored)
        return
      }
    } catch { }
    setCurrentPage('create')
  }

  function handleProceedPrevious() {
    localStorage.setItem('crm_restore_previous', '1')
    setPrevTicketModal(null)
    setCurrentPage('create')
  }

  function handleCreateNew() {
    localStorage.removeItem('ozonetel_auto_ticket')
    localStorage.removeItem('ozonetel_call_log_id')
    localStorage.removeItem('ozonetel_call_data')
    if (clearCallState) clearCallState()
    setPrevTicketModal(null)
    setCurrentPage('create')
  }

  const debouncedQuery = useDebounce(searchValue, 300)

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false)
      if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Live search
  useEffect(() => {
    const q = debouncedQuery.trim()
    if (!q || !fetchJson) { setSearchResults([]); setSearchOpen(false); return }
    let active = true
    setSearchLoading(true)
    fetchJson(`/api/agents/search-bar?q=${encodeURIComponent(q)}`)
      .then((data) => { if (active) { setSearchResults(data?.results || []); setSearchOpen(true) } })
      .catch(() => { if (active) setSearchResults([]) })
      .finally(() => { if (active) setSearchLoading(false) })
    return () => { active = false }
  }, [debouncedQuery, fetchJson])

  function selectPeriod(opt) {
    const today = new Date()
    const fmt = (d) => d.toISOString().slice(0, 10)
    const toDate = fmt(today)
    let fromDate = toDate
    if (opt.period === '7days') {
      const d = new Date(today); d.setDate(d.getDate() - 6); fromDate = fmt(d)
    } else if (opt.period === '30days') {
      const d = new Date(today); d.setDate(d.getDate() - 29); fromDate = fmt(d)
    } else if (opt.period === '6months') {
      const d = new Date(today); d.setMonth(d.getMonth() - 6); fromDate = fmt(d)
    }
    setDateFilter({ period: opt.period, label: opt.label, fromDate, toDate })
    setDateOpen(false)
  }

  function applyCustom() {
    if (!customFrom || !customTo) return
    setDateFilter({ period: 'custom', label: `${customFrom} – ${customTo}`, fromDate: customFrom, toDate: customTo })
    setDateOpen(false)
  }

  function handleResultClick(result) {
    setSearchOpen(false); setSearchValue('')
    if (result.card_type === 'ticket') {
      if (setSelectedTicket) setSelectedTicket(result)
      setCurrentPage('ticket-detail')
      return
    }
    if (setSelectedVehicle) setSelectedVehicle(result)
    setCurrentPage('vehicle-360')
  }

  return (
    <div style={{
      height: '50px', background: '#FFFFFF', borderBottom: '1px solid #E0E7EF',
      boxShadow: '0 2px 4px rgba(0,0,0,.06)',
      display: 'flex', alignItems: 'center', gap: '10px', padding: '0 16px', flexShrink: 0,
    }}>
      {/* Sidebar toggle — only when sidebar is collapsed */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          title="Open sidebar"
          style={{
            width: 32, height: 32, flexShrink: 0,
            background: '#F1F5F9', border: '1px solid #E0E7EF',
            borderRadius: 6, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: '#5A6672',
          }}
        >
          ☰
        </button>
      )}

      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '15px', fontWeight: 600, color: '#2B2B2B', whiteSpace: 'nowrap' }}>
          {pageTitle[currentPage] || 'Dashboard'}
        </span>
        {currentPage === 'agent' && (
          <button
            onClick={handleCreateTicketClick}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 12px', background: '#0090D9', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            + Create Ticket
          </button>
        )}
      </div>

      {/* Date filter — agent page only */}
      {currentPage === 'agent' && (
        <div ref={dateRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDateOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', background: '#fff', border: '1px solid #E0E7EF',
              borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
              color: '#5A6672', whiteSpace: 'nowrap',
            }}
          >
            <CalendarIcon />
            {dateFilter?.label || 'Today'}
            <ChevronDown />
          </button>
          {dateOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0,
              background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,.1)', minWidth: '220px', zIndex: 1000,
            }}>
              <div style={{ padding: '6px 0', borderBottom: '1px solid #E0E7EF' }}>
                {DATE_OPTIONS.map(opt => (
                  <div
                    key={opt.period}
                    onClick={() => selectPeriod(opt)}
                    style={{
                      padding: '8px 14px', fontSize: '12px', cursor: 'pointer',
                      color: dateFilter?.period === opt.period ? '#0090D9' : '#2B2B2B',
                      background: dateFilter?.period === opt.period ? '#E5F4FB' : 'transparent',
                      fontWeight: dateFilter?.period === opt.period ? 500 : 400,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = dateFilter?.period === opt.period ? '#E5F4FB' : '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = dateFilter?.period === opt.period ? '#E5F4FB' : 'transparent'}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: '8px' }}>Custom Range</div>
                <div style={{ marginBottom: '6px' }}>
                  <label style={{ fontSize: '11px', color: '#5A6672', display: 'block', marginBottom: '3px' }}>From</label>
                  <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: '4px', fontSize: '11px', outline: 'none' }} />
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '11px', color: '#5A6672', display: 'block', marginBottom: '3px' }}>To</label>
                  <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                    style={{ width: '100%', padding: '5px 7px', border: '1px solid #E0E7EF', borderRadius: '4px', fontSize: '11px', outline: 'none' }} />
                </div>
                <button onClick={applyCustom} style={{
                  width: '100%', padding: '6px', background: '#0090D9', color: '#fff',
                  border: 'none', borderRadius: '5px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                }}>Apply</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search Box */}
      <div ref={searchRef} style={{ position: 'relative' }}>
        <div style={{
          display: 'flex', alignItems: 'center', background: '#F8FAFB',
          border: '1px solid #E0E7EF', borderRadius: '6px',
          padding: '0 9px', gap: '7px', width: '220px',
        }}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search ticket ID, VIN, mobile…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => searchResults.length && setSearchOpen(true)}
            style={{ border: 'none', background: 'transparent', fontSize: '12px', color: '#2B2B2B', outline: 'none', width: '100%', height: '30px' }}
          />
          {searchLoading && (
            <div style={{ width: '10px', height: '10px', border: '2px solid #CBD5E1', borderTopColor: '#0090D9', borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0 }} />
          )}
        </div>

        {searchOpen && searchResults.length > 0 && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: '400px',
            background: '#FFFFFF', border: '1px solid #E0E7EF', borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,.10)', zIndex: 1000, overflow: 'hidden',
          }}>
            {searchResults.map((r, i) => (
              <div key={i} onClick={() => handleResultClick(r)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? '1px solid #F1F5F9' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
              >
                {r.card_type === 'ticket' ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontWeight: 700, fontSize: '13px', color: '#2B2B2B' }}>{r.ticket_number}</span>
                      <StatusBadge status={r.ticket_status} />
                    </div>
                    <div style={{ fontSize: '11px', color: '#5A6672' }}>
                      {[r.customer_name, r.customer_phone ? `· ${r.customer_phone}` : null].filter(Boolean).join(' ')}
                    </div>
                    {(r.category || r.sub_category) && (
                      <div style={{ fontSize: '11px', color: '#9AA5B1', marginTop: '2px' }}>{[r.category, r.sub_category].filter(Boolean).join(' — ')}</div>
                    )}
                  </div>
                ) : (
                  <div>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px', lineHeight: 1 }}>🚗</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '14px', color: '#2B2B2B', fontFamily: "'Courier New',monospace" }}>{r.vin_number}</div>
                          <div style={{ fontSize: '11px', color: '#5A6672', marginTop: '2px' }}>
                            {[r.registration_number, r.model, r.oem].filter(Boolean).join(' · ')}
                            {r.vehicle_state && (
                              <span style={{ marginLeft: '6px', color: '#27AE60', fontWeight: 600 }}>● {r.vehicle_state.charAt(0).toUpperCase() + r.vehicle_state.slice(1)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleResultClick(r) }}
                        style={{
                          flexShrink: 0, padding: '6px 12px', background: '#1B4FD8', color: '#fff',
                          border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        Open Vehicle 360 ↗
                      </button>
                    </div>

                    {/* Account box only */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px', marginBottom: '10px' }}>
                      <div style={{ background: '#F8FAFB', border: '1px solid #E0E7EF', borderRadius: '6px', padding: '8px 12px' }}>
                        <div style={{ fontSize: '9px', fontWeight: 600, color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '3px' }}>Account</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#2B2B2B' }}>
                          {r.account_type ? r.account_type.charAt(0).toUpperCase() + r.account_type.slice(1) : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Ticket History */}
                    {r.tickets && r.tickets.length > 0 && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '6px' }}>
                          Ticket History ({r.tickets.length})
                        </div>
                        {r.tickets.map((t, ti) => {
                          const sc = t.ticket_status === 'in_progress' ? '#F59E0B'
                            : t.ticket_status === 'resolved' ? '#27AE60'
                            : t.ticket_status === 'closed' ? '#94A3B8' : '#3B82F6'
                          const sl = t.ticket_status === 'in_progress' ? 'In Progress'
                            : t.ticket_status === 'resolved' ? 'Resolved'
                            : t.ticket_status === 'closed' ? 'Closed'
                            : t.ticket_status || '—'
                          return (
                            <div key={ti} style={{
                              background: '#fff', border: '1px solid #E0E7EF', borderRadius: '6px',
                              padding: '8px 12px', marginBottom: '4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#1B4FD8' }}>{t.ticket_number}</div>
                                <div style={{ fontSize: '11px', color: '#5A6672', marginTop: '1px' }}>{t.sub_category || t.category || '—'}</div>
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: 600, color: sc }}>{sl}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {searchOpen && !searchLoading && searchValue.trim() && searchResults.length === 0 && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: '260px',
            background: '#FFFFFF', border: '1px solid #E0E7EF', borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,.10)', zIndex: 1000,
            padding: '14px', textAlign: 'center', fontSize: '12px', color: '#9AA5B1',
          }}>
            No results found for "{searchValue}"
          </div>
        )}
      </div>

      {/* Previous ticket modal */}
      {prevTicketModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px', width: 420, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', position: 'relative',
          }}>
            <button
              onClick={() => setPrevTicketModal(null)}
              style={{
                position: 'absolute', top: 12, right: 14,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: '#9CA3AF', lineHeight: 1, padding: 0,
              }}
            >×</button>
            <div style={{ fontSize: 20, marginBottom: 8 }}>🎫</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Previous ticket data found
            </div>
            <div style={{ background: '#F8FAFC', border: '1px solid #E0E7EF', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              {prevTicketModal.ticket_number && (
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1B4FD8', marginBottom: 4 }}>
                  #{prevTicketModal.ticket_number}
                </div>
              )}
              {prevTicketModal.customer_details?.name && (
                <div style={{ fontSize: 12, color: '#374151' }}>
                  {prevTicketModal.customer_details.name}
                  {prevTicketModal.customer_details.phone && (
                    <span style={{ color: '#9CA3AF', marginLeft: 8 }}>{prevTicketModal.customer_details.phone}</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              Do you want to proceed with the previous ticket or start a new one?
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleProceedPrevious}
                style={{ flex: 1, padding: '10px 0', background: '#0090D9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Proceed with previous
              </button>
              <button
                onClick={handleCreateNew}
                style={{ flex: 1, padding: '10px 0', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Create new
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialer */}
      <div
        onClick={() => setDialerOpen && setDialerOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px',
          borderRadius: '20px',
          background: dialerOpen ? '#2B7DB8' : '#EBF5FB',
          border: `1px solid ${dialerOpen ? '#1A6FA8' : '#A8D4EE'}`,
          cursor: 'pointer', fontSize: '12px', fontWeight: 600,
          color: dialerOpen ? '#fff' : '#2B7DB8', flexShrink: 0,
          transition: 'background .15s, color .15s',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke={dialerOpen ? '#fff' : '#56A0D3'} strokeWidth="1.8">
          <path d="M6.5 4S5 4 4 6s1 5 3 7 5 4 7 3 2-2.5 2-2.5l-3-3-2 1-3-4 1-2-3-3z" />
        </svg>
        Dialer
      </div>

    </div>
  )
}

export default AdminTopbar
